import { type Chart, type FontSpec } from "chart.js";
import { set } from "lodash";
import { type ChartJSOrUndefined } from "react-chartjs-2/dist/types";

import { type SalesPageMetric } from "@north-beam/nb-client-side";
import { type CurrencyCode } from "@north-beam/nb-common";

import {
  drawAlternativeMetricTitle,
  drawMetricTitle,
} from "../../utils/canvas";
import { METRIC_ORDER } from "../../utils/chart";

import { checkShouldDrawChartLegendInChart } from "./split-chart-data-view-plugin";

export const Y_SCALE_TITLES_PLUGIN_ID = "yScaleTitles";

export type YScaleTitlesPluginPluginOptions = {
  titles?: Record<string, string>;
  font?: FontSpec;
};

export const yScaleTitlesPlugin = {
  id: Y_SCALE_TITLES_PLUGIN_ID,
  options: {
    titles: {},
    font: {},
  } as YScaleTitlesPluginPluginOptions,
  afterDraw(
    chart: Chart,
    _args: any,
    options: YScaleTitlesPluginPluginOptions,
  ) {
    if (checkShouldDrawChartLegendInChart(chart)) {
      return;
    }

    const {
      ctx,
      chartArea,
      scales: { compare1, compare2 },
      width,
      config,
    } = chart;
    const { top } = chartArea;
    const font = options.font as FontSpec;
    const numberOfMetrics =
      [compare1.options.display, compare2.options.display].filter(Boolean)
        .length + 1;

    const mainMetricName = options.titles?.mainMetric ?? "";
    const compare1Name = options.titles?.compare1 ?? "";
    const compare2Name = options.titles?.compare2 ?? "";
    const [mainMetricConfig, compare1Config, compare2Config] =
      config.data.datasets;

    if (numberOfMetrics < 3) {
      drawMetricTitle(ctx, mainMetricName, {
        top,
        titleXPosition: 0,
        font,
      });
      ctx.font = `${font.weight} ${font.size}px ${font.family}`;
      const compare1Width = ctx.measureText(compare1Name).width;
      drawMetricTitle(ctx, compare1Name, {
        top,
        titleXPosition: width - compare1Width,
        font,
      });
    } else {
      drawAlternativeMetricTitle(ctx, mainMetricName, {
        font,
        x: 8,
        tickWidth: 12,
        chartArea,
        chart,
        config: mainMetricConfig,
      });

      drawAlternativeMetricTitle(ctx, compare1Name, {
        font,
        x: compare1.right - 10,
        tickWidth: -12,
        chartArea,
        chart,
        config: compare1Config,
      });

      drawAlternativeMetricTitle(ctx, compare2Name, {
        font,
        x: compare2.right - 2,
        tickWidth: -12,
        chartArea,
        chart,
        config: compare2Config,
      });
    }
  },
};

type SetYAxesTitlesParams = {
  chart: ChartJSOrUndefined | null;
  selectedMetrics: SalesPageMetric[];
  code: CurrencyCode;
};

export const setYAxesTitles = ({
  chart,
  selectedMetrics,
  code,
}: SetYAxesTitlesParams) => {
  if (chart) {
    METRIC_ORDER.forEach((metricId, index) => {
      const metric = selectedMetrics[index];
      set(
        chart,
        `config.options.plugins.${Y_SCALE_TITLES_PLUGIN_ID}.titles.${metricId}`,
        metric?.name ?? "",
      );

      if (metric) {
        set(
          chart,
          `config.options.scales.${metricId}.ticks.callback`,
          (v: number | null | undefined) =>
            metric.formatter(v, code, {
              precision: "approximate",
            }),
        );
        set(chart, `config.options.scales.${metricId}.display`, true);
      } else {
        set(chart, `config.options.scales.${metricId}.display`, false);
      }
    });
    setYAxesTitlesPadding(chart, selectedMetrics.length);
    chart.update();
  }
};

// Makes the chart title padding updates
const setYAxesTitlesPadding = (chart: Chart, selectedMetricsLength: number) => {
  if (selectedMetricsLength > 2) {
    set(chart, "config.options.scales.mainMetric.title.display", true);
    set(chart, "config.options.scales.mainMetric.title.padding", 0);
    set(chart, "config.options.scales.compare1.title.display", true);
    set(chart, "config.options.scales.compare1.title.padding", 8);
    set(chart, "config.options.scales.compare2.title.display", true);
    set(chart, "config.options.scales.compare2.title.padding", 0);
  } else {
    set(chart, "config.options.scales.mainMetric.title.display", false);
    set(chart, "config.options.scales.mainMetric.title.padding", 0);
    set(chart, "config.options.scales.compare1.title.display", false);
    set(chart, "config.options.scales.compare1.title.padding", 0);
    set(chart, "config.options.scales.compare2.title.display", false);
    set(chart, "config.options.scales.compare2.title.padding", 0);
  }
};
